'use client';

import { getGlobalInitData } from '@/api/public';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  buildGlobalInitRequestPayload,
  mapGlobalInitResponseToChromeState,
  unwrapGlobalInitResponse,
  type GlobalInitChromeMapped,
} from '@/utils/map-global-init-data';
import { usePrevious } from 'ahooks';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 唯一入口：客户端 global init，驱动顶栏/底栏与 Store 中与 init 相关的字段。
 * 依赖含 token，登录态变化时会与路由/query 变化走同一套映射逻辑。
 *
 * @param initialMappedData 服务端已获取并映射好的 global init 数据；若存在，首屏不拉取、不展示骨架屏。
 */
export function useGlobalShellInit(
  deviceType: EPlatform,
  initialMappedData?: Partial<GlobalInitChromeMapped>,
) {
  const pathname = usePathname();
  const token = useStore((s) => s.token);

  const [mapped, setMapped] = useState<Partial<GlobalInitChromeMapped>>(initialMappedData || {});
  /** global init 请求进行中（含依赖变化触发的重拉），供顶栏骨架使用 */
  const [isLoading, setIsLoading] = useState(!initialMappedData);

  const setCurrencyList = useStore((s) => s.setCurrencyList);
  const setCountryList = useStore((s) => s.setCountryList);
  const setCountrySiteList = useStore((s) => s.setCountrySiteList);
  const setPublicResourceData = useStore((s) => s.setPublicResourceData);
  const setMemberLevelConfig = useStore((s) => s.setMemberLevelConfig);
  const setCompliancePolicies = useStore((s) => s.setCompliancePolicies);
  const prevToken = usePrevious(token);
  const isInitialEffect = useRef(true);
  const hadInitialData = useRef(!!initialMappedData);

  const refreshPublicResourceData = useCallback(
    async (silent = false) => {
      try {
        const isWeb = +deviceType === EPlatform.WEB;
        const isHomePreview = pathname?.includes(HOME_PREVIEW_PATH) ?? false;
        let storeId = isWeb ? EPlatform.WEB : EPlatform.WAP;
        if (
          isHomePreview &&
          typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('type') === '3'
        ) {
          storeId = EPlatform.IOS;
        }
        if (!silent) {
          setIsLoading(true);
        }
        const payload = buildGlobalInitRequestPayload(isHomePreview);
        const res = await getGlobalInitData(isWeb, payload, { storeIdOverride: storeId });
        const raw = unwrapGlobalInitResponse(res);
        const next = mapGlobalInitResponseToChromeState(raw, isWeb);
        setMapped(next);
        setCurrencyList(next.currencyListNormalized);
        setCountryList(next.countryList);
        setCountrySiteList(next.countrySiteList);
        setPublicResourceData(next.publicResourceData);
        setCompliancePolicies(next.compliancePolicies ?? null);
        if (Array.isArray(next.memberLevelData?.data)) {
          setMemberLevelConfig(next.memberLevelData.data);
        }
      } catch (e) {
        logger.error(e, 'useGlobalShellInit: getGlobalInitData failed');
        setMapped((current) => ({ ...current, dtInfo: null }));
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [
      deviceType,
      pathname,
      setCurrencyList,
      setCountryList,
      setCountrySiteList,
      setPublicResourceData,
      setMemberLevelConfig,
      setCompliancePolicies,
    ],
  );

  useEffect(() => {
    // 首次 effect 执行（水合完成后）
    if (isInitialEffect.current) {
      isInitialEffect.current = false;
      if (!hadInitialData.current) {
        // 无服务端初始数据，正常拉取（可展示骨架屏）
        refreshPublicResourceData(false);
      } else if (token) {
        // 有初始数据但用户已登录，服务端大概率不带登录态，静默刷新以获取登录态数据（不展示骨架屏）
        refreshPublicResourceData(true);
      }
      return;
    }

    // 后续 token 变化（登录 / 登出 / 换号），静默刷新不展示骨架屏
    if (token !== prevToken) {
      refreshPublicResourceData(true);
    }
  }, [token, prevToken, refreshPublicResourceData]);

  return { mapped, isLoading };
}
